import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ConnectableObservable, Observable, of } from "rxjs";
import { publishLast, tap } from "rxjs/operators";
import { ISecureStorage } from "../../classes/interfaces/isecurestorage";

import { withSpinner } from "../../extensions/withSpinner.extension";
import { Contact, EmailMessage, Recordable, RecordableAttachment, RecordableCategory, RecordableDivisionDefault, ScheduleTask, SearchRecordablesViewModel, StagedBlob, Vendor } from "../../types";
import { API_ROOT, SECURE_LOCAL_STORAGE } from "../../types/constants/appconstants";

@Injectable({
    "providedIn": "root"
})
export class RecordablesService {
    private http = inject(HttpClient);
    private apiRoot = inject(API_ROOT);
    private storage = inject<ISecureStorage>(SECURE_LOCAL_STORAGE);
    private recordablesBaseUrl = this.apiRoot + "api/recordables";
    private lastMarketId: number;
    private marketVendors: ConnectableObservable<Vendor[]>;
    private recordableContactsStorageKey: string = "RecordableContacts";

    searchRecordables(searchRecordables: SearchRecordablesViewModel): Observable<Recordable[]> {
        return withSpinner(this.http).post<Recordable[]>(`${this.recordablesBaseUrl}/search`, searchRecordables);
    }

    getRecordableCategories(): Observable<RecordableCategory[]> {
        return withSpinner(this.http).get<RecordableCategory[]>(`${this.recordablesBaseUrl}/categories`);
    }

    getRecordableContacts(marketNumber: string): Observable<Contact[]> {
        var cacheContacts = this.storage.getItem<Contact[]>(`${this.recordableContactsStorageKey}_${marketNumber}`) as Contact[];
        if (cacheContacts) {
            const contacts$ = of(cacheContacts);
            return contacts$;
        }

        const result$ = withSpinner(this.http).get<Contact[]>(`${this.recordablesBaseUrl}/contacts/${marketNumber}`).pipe(
            tap((contacts: Contact[]) => this.storage.setItem(`${this.recordableContactsStorageKey}_${marketNumber}`, contacts))
        );

        return result$;
    }

    getScheduleTasksForLot(lotId: number): Observable<ScheduleTask[]> {
        return withSpinner(this.http).get<ScheduleTask[]>(`${this.apiRoot}api/scheduletasks/lot/${lotId}`);
    }

    getRecordablesForScheduleTask(marketId: number, scheduleTaskId: number): Observable<Recordable[]> {
        return withSpinner(this.http).get<Recordable[]>(`${this.recordablesBaseUrl}/${marketId}/scheduleTask/${scheduleTaskId}`);
    }

    deleteRecordable(recordables: Recordable[]): Observable<any> {
        return withSpinner(this.http).post(`${this.recordablesBaseUrl}/deleterecordables`, recordables);
    }

    saveRecordable(recordable: Recordable): Observable<Recordable> {
        if (recordable.id === 0) {
            return withSpinner(this.http).post<Recordable>(`${this.recordablesBaseUrl}`, recordable);
        } else {
            return withSpinner(this.http).put<Recordable>(`${this.recordablesBaseUrl}`, recordable);
        }
    }

    getPendingStatus(recordableId: number): Observable<boolean> {
        return withSpinner(this.http).get<boolean>(`${this.recordablesBaseUrl}/${recordableId}/pendingstatus`);
    }

    getRecordableAttachments(recordableId: number): Observable<RecordableAttachment[]> {
        return withSpinner(this.http).get<RecordableAttachment[]>(`${this.recordablesBaseUrl}/${recordableId}/recordableattachments`);
    }

    getRecordableAttachment(recordableId: number, recordableAttachmentId: number): Observable<RecordableAttachment> {
        return withSpinner(this.http).get<RecordableAttachment>(`${this.recordablesBaseUrl}/recordableattachment/${recordableId}/${recordableAttachmentId}`);
    }

    getPendingRecordableAttachments(recordableId: number): Observable<StagedBlob[]> {
        return withSpinner(this.http).get<StagedBlob[]>(`${this.recordablesBaseUrl}/recordableattachment/${recordableId}/pendingattachments`);
    }

    getDownloadUri(attachment: RecordableAttachment): Observable<string> {
        return withSpinner(this.http).post<string>(`${this.recordablesBaseUrl}/downloaduri`, attachment);
    }

    getMarketVendors(marketId: number): Observable<Vendor[]> {
        if (!this.marketVendors || marketId != this.lastMarketId) {
            this.marketVendors = withSpinner(this.http).get<Vendor[]>(`${this.recordablesBaseUrl}/vendors/${marketId}`).pipe(
                publishLast()
            ) as ConnectableObservable<Vendor[]>;
            this.marketVendors.connect();

            this.lastMarketId = marketId
        }

        return this.marketVendors
    }

    getDivisionSettings(marketId: number): Observable<RecordableDivisionDefault> {
        return withSpinner(this.http).get<RecordableDivisionDefault>(`${this.recordablesBaseUrl}/${marketId}/divisionsettings`);
    }

    updateDivisionSettings(recordableDivisionDefaults: RecordableDivisionDefault): Observable<RecordableDivisionDefault> {
        return withSpinner(this.http).post<RecordableDivisionDefault>(`${this.recordablesBaseUrl}/divisionsettings`, recordableDivisionDefaults);
    }

    massCloseRecordables(recordables: Recordable[]): Observable<any> {
        return withSpinner(this.http).post(`${this.recordablesBaseUrl}/masscloserecordables`, recordables);
    }

    sendRecordableEmail(emailMessage: EmailMessage, recordable: Recordable, recipients: string[]): Observable<boolean> {
        return withSpinner(this.http).post<boolean>(`${this.recordablesBaseUrl}/sendemail`, { emailMessage: emailMessage, recordable: recordable, recipients: recipients });
    }
}
